import * as Sentry from '@sentry/react';
import { HttpError } from 'api/api-client/HttpError';
import i18next from 'i18next';
import {
  ApiSourceEnum,
  BackendError,
  DelParams,
  FetchRequest,
  GetParams,
  PostParams,
  PutParams,
} from './dtos';
import { addAuthorizationHeader, parseUrl, refreshToken } from './utils';

export class ApiClient {
  private isRefreshingToken = false;
  private refreshPromise: Promise<void> | null = null;

  constructor(private apiSource: ApiSourceEnum) {}

  private async myFetch<T>(data: FetchRequest): Promise<T> {
    if (data.url.startsWith('/')) data.url = data.url.substring(1);

    const { method, url, businessId, body, queryStringParameters } = data;

    let headers = new Headers({
      'Content-Type': 'application/json',
      ...data.headers,
    });

    if (this.apiSource === ApiSourceEnum.API_AUTH) headers = addAuthorizationHeader(headers);

    const parsedUrl = parseUrl(this.apiSource, url, businessId, queryStringParameters);

    let parsedBody = body;

    if (data.body instanceof FormData) {
      headers.delete('Content-Type');
    } else {
      parsedBody = JSON.stringify(body);
    }

    try {
      const fetchResponse = await fetch(parsedUrl.toString(), {
        method,
        headers,
        body: parsedBody as BodyInit,
      });

      if (fetchResponse.status === 204) {
        return undefined as T;
      }

      if (fetchResponse.status !== 200) {
        let error: BackendError;

        try {
          error = (await fetchResponse.json()) as BackendError;
        } catch {
          error = {
            name: 'Error',
            message: i18next.t('errors.generic.fallback_message'),
            errors: [],
          };
        }

        // Refresh token
        if (fetchResponse.status === 401 && error.message === 'Token expired') {
          if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            this.refreshPromise = refreshToken().then(() => {
              this.isRefreshingToken = false;
              this.refreshPromise = null;
            });
          }

          await this.refreshPromise;

          return this.myFetch<T>(data);
        }

        throw new HttpError(fetchResponse.status, error, {
          url: parsedUrl.toString(),
          method,
          data: parsedBody,
        });
      }

      return fetchResponse.json() as T;
    } catch (error) {
      if (error instanceof Error) {
        // This is made for Sentry to know why the hell we are getting so many errors
        if (error.message === 'Load failed' || error.message === 'Failed to fetch') {
          console.error('Fetch error.');
          Sentry.setExtras({
            method: data.method,
            url: parsedUrl.toString(),
            data: parsedBody,
            params: data.queryStringParameters,
          });
          Sentry.captureException(error);

          throw new HttpError(
            500,
            {
              name: 'Error',
              message: i18next.t('errors.network.fetch_failed'),
              errors: [],
            },
            {
              url: parsedUrl.toString(),
              method,
              data: parsedBody,
            },
          );
        }
      }

      throw error;
    }
  }

  get = async <T>(businessId: number | undefined, url: string, data?: GetParams) =>
    this.myFetch<T>({
      url,
      method: 'GET',
      businessId: businessId,
      ...data,
    });

  post = async <T>(businessId: number | undefined, url: string, data?: PostParams) =>
    this.myFetch<T>({
      method: 'POST',
      url,
      businessId: businessId,
      body: data?.body,
      ...data,
    });

  put = async <T>(businessId: number | undefined, url: string, data?: PutParams) =>
    this.myFetch<T>({
      method: 'PUT',
      url,
      businessId: businessId,
      body: data?.body,
      ...data,
    });

  del = async <T = undefined>(businessId: number | undefined, url: string, data?: DelParams) =>
    this.myFetch<T>({
      method: 'DELETE',
      url: url,
      businessId: businessId,
      ...data,
    });
}
